import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Statements.css";
import DpHoldingsView from "./Statements/DpHoldingsView";
import DpLedgerView from "./Statements/DpLedgerView";
import DpTransactionView from "./Statements/DpTransactionView";
import decrypted from "../helper/decrpted";
import { getClientListByUserCodeApi } from "../api/fakebackend_helper";

const extractRawList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;

  if (typeof res === "object") {
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.list)) return res.list;
    if (Array.isArray(res.result)) return res.result;

    if (res.data && typeof res.data === "object") {
      if (Array.isArray(res.data.data)) return res.data.data;
      if (Array.isArray(res.data.list)) return res.data.list;
      if (Array.isArray(res.data.result)) return res.data.result;

      if (res.data.oowncode) {
        return [res.data];
      }

      const subValues = Object.values(res.data);
      if (subValues.length > 0 && typeof subValues[0] === "object") {
        return subValues;
      }
    }

    if (
      res.oowncode
    ) {
      return [res];
    }

    const values = Object.values(res);
    if (values.length > 0 && typeof values[0] === "object") {
      return values;
    }
  }

  return [];
};

const Statements = () => {
  const [activeTab, setActiveTab] = useState("holdings");
  const [inputClientCode, setInputClientCode] = useState("");
  const [submittedClientCode, setSubmittedClientCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [clientList, setClientList] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const decryptedResult = useMemo(() => {
    try {
      const userData = JSON.parse(
        sessionStorage.getItem("userData") ||
        localStorage.getItem("userData") ||
        "null"
      );
      return decrypted(userData);
    } catch (e) {
      return null;
    }
  }, []);

  const rmUsername = useMemo(() => {
    return (
      decryptedResult?.username ||
      decryptedResult?.user_code ||
      decryptedResult?.rm_code ||
      decryptedResult?.code ||
      localStorage.getItem("username") ||
      localStorage.getItem("user_id") ||
      ""
    ).trim();
  }, [decryptedResult]);

  useEffect(() => {
    const fetchRMClientList = async () => {
      if (!rmUsername) return;
      setLoadingClients(true);
      try {
        const res = await getClientListByUserCodeApi(rmUsername);
        if (res) {
          const rawList = extractRawList(res);

          const uniqueMap = new Map();
          rawList.forEach((item) => {
            let code = "";
            let name = "";
            if (typeof item === "string") {
              code = item.trim();
            } else if (item && typeof item === "object") {
              code = (
                item.oowncode ||
                item.OOWNCODE ||
                item.oown_code ||
                item.owncode ||
                item.OWNCODE ||
                item.own_code ||
                item.code ||
                item.client_code ||
                item.clientCode ||
                item.user_code ||
                item.clientid ||
                item.CLIENTCODE ||
                ""
              ).toString().trim();

              name = (
                item.client_name ||
                item.name ||
                item.clientName ||
                item.customer_name ||
                item.CLIENTNAME ||
                ""
              ).toString().trim();
            }

            if (code && !uniqueMap.has(code.toLowerCase())) {
              uniqueMap.set(code.toLowerCase(), {
                code,
                name,
                label: name ? `${code} - ${name}` : code,
              });
            }
          });

          const parsed = Array.from(uniqueMap.values());
          setClientList(parsed);
        }
      } catch (err) {
        console.error("Failed to fetch RM client list:", err);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchRMClientList();
  }, [rmUsername]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredClients = useMemo(() => {
    const search = inputClientCode.trim().toLowerCase();
    if (!search) return clientList.slice(0, 50);
    return clientList
      .filter(
        (item) =>
          item.code.toLowerCase().includes(search) ||
          item.name.toLowerCase().includes(search) ||
          item.label.toLowerCase().includes(search)
      )
      .slice(0, 50);
  }, [clientList, inputClientCode]);

  const handleSelectClient = (code) => {
    const matchedItem = clientList.find(
      (item) => item.code.toLowerCase() === code.trim().toLowerCase()
    );

    if (matchedItem) {
      setInputClientCode(matchedItem.code);
      setSubmittedClientCode(matchedItem.code);
      setErrorMessage("");
    } else {
      setSubmittedClientCode("");
      setErrorMessage("Please enter valid client code");
    }
    setShowDropdown(false);
  };

  const handleClientCodeSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = inputClientCode.trim();

    if (!trimmed) {
      setSubmittedClientCode("");
      setErrorMessage("Please enter valid client code");
      setShowDropdown(false);
      return;
    }

    // Validation Check: Must match an owncode from API response list
    const matchedItem = clientList.find(
      (item) => item.code.toLowerCase() === trimmed.toLowerCase()
    );

    if (matchedItem) {
      // Valid & Matched owncode!
      setSubmittedClientCode(matchedItem.code);
      setInputClientCode(matchedItem.code);
      setErrorMessage("");
      setShowDropdown(false);
    } else {
      // Invalid code / Not matching API owncode list
      setSubmittedClientCode("");
      setErrorMessage("Please enter valid client code");
      setShowDropdown(false);
    }
  };

  const handleClearInput = () => {
    setInputClientCode("");
    setSubmittedClientCode("");
    setErrorMessage("");
    setShowDropdown(false);
  };

  return (
    <div className="statements-wrapper">
      <div className="statements-container">
        <div className="statements-page-header">
          <h1 className="statements-page-title">My Statements</h1>
          <div className="statements-breadcrumb">
            Dashboard &gt; <span>My Statements</span>
          </div>
        </div>

        <div className="statements-card">
          {/* Client Code Header Input Banner */}
          <div className="statements-clientcode-banner">
            <h4 className="statements-clientcode-title">Select Client Code:</h4>
            <div
              className="statements-clientcode-form"
              ref={dropdownRef}
              style={{ position: "relative" }}
            >
              <div className="statements-clientcode-container">
                <input
                  type="text"
                  className={`statements-clientcode-input ${errorMessage ? "is-invalid" : ""}`}
                  placeholder={
                    loadingClients
                      ? "Loading RM Clients..."
                      : "Search Client Code (e.g. Y090...)"
                  }
                  value={inputClientCode}
                  onFocus={() => setShowDropdown(true)}
                  onChange={(e) => {
                    setInputClientCode(e.target.value);
                    if (errorMessage) setErrorMessage("");
                    setShowDropdown(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleClientCodeSubmit(e);
                    }
                  }}
                />
                {inputClientCode && (
                  <button
                    type="button"
                    className="btn-clientcode-clear"
                    onClick={handleClearInput}
                    title="Clear text"
                  >
                    ✕
                  </button>
                )}
                {showDropdown && filteredClients.length > 0 && (
                  <div className="statements-autocomplete-dropdown">
                    {filteredClients.map((client, idx) => (
                      <div
                        key={idx}
                        className="statements-autocomplete-item"
                        onClick={() => handleSelectClient(client.code)}
                      >
                        <span className="statements-autocomplete-code">
                          {client.code}
                        </span>
                        {client.name && (
                          <span className="statements-autocomplete-name">
                            {client.name}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn-clientcode-submit"
                onClick={handleClientCodeSubmit}
              >
                Fetch Statements
              </button>
            </div>
            {errorMessage && (
              <span className="statements-error-badge" style={{ color: "#ef4444", fontSize: "13px", fontWeight: "600" }}>
                ⚠️ {errorMessage}
              </span>
            )}
            {clientList.length > 0 && !errorMessage && (
              <span className="statements-rm-badge">
                RM ({rmUsername}): {clientList.length} Clients
              </span>
            )}
            {submittedClientCode && (
              <div className="ms-auto fs-13 text-muted">
                Active Client:{" "}
                <strong className="text-primary">{submittedClientCode}</strong>
              </div>
            )}
          </div>

          {/* Top Level Sub-Tabs Bar: ONLY DP Holdings, DP Ledger, and DP Transaction */}
          <div className="statements-tabs-bar">
            <button
              className={`statements-tab-item ${activeTab === "holdings" ? "active" : ""
                }`}
              onClick={() => setActiveTab("holdings")}
            >
              DP Holdings
            </button>
            <button
              className={`statements-tab-item ${activeTab === "ledger" ? "active" : ""
                }`}
              onClick={() => setActiveTab("ledger")}
            >
              DP Ledger
            </button>
            <button
              className={`statements-tab-item ${activeTab === "transaction" ? "active" : ""
                }`}
              onClick={() => setActiveTab("transaction")}
            >
              DP Transaction
            </button>
          </div>

          <div className="statements-content-body">
            {!submittedClientCode ? (
              <div className={`statements-prompt-box ${errorMessage ? "error-prompt" : ""}`}>
                {errorMessage ? (
                  <>
                    <h4 style={{ color: "#ef4444" }}>Invalid Client Code</h4>
                    {/* <p className="m-0" style={{ color: "#ef4444", fontWeight: "500" }}>
                      Please enter valid client code. The entered code is not present in the assigned RM client list.
                    </p> */}
                  </>
                ) : (
                  <>
                    <h4>Client Code Required</h4>
                    {/* <p className="m-0">
                      Please select or enter a valid Client Code assigned to your RM code above and click <strong>Fetch Statements</strong> to view DP Holdings, DP Ledger, and DP Transactions.
                    </p> */}
                  </>
                )}
              </div>
            ) : (
              <>
                {activeTab === "holdings" && (
                  <DpHoldingsView clientCode={submittedClientCode} />
                )}
                {activeTab === "ledger" && (
                  <DpLedgerView clientCode={submittedClientCode} />
                )}
                {activeTab === "transaction" && (
                  <DpTransactionView clientCode={submittedClientCode} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statements;
